import React, { useEffect, useMemo, useRef } from "react";
import styles from "./AISelfieGenerator.module.css";
import { Button, Container, Typography, Box, Grid } from "@mui/material";
import Navbar from "../../Navbar";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Footer from "../../Footer";
import FAQ from "./FAQ";
import PricingCards from "../../PricingCards";
import ImageSlider from "../../ImageSlider";
import ImageSlider2 from "../../ImageSlider2";
import DownloadSection from "../../DownloadSection";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const AISelfieGenerator = () => {
  const seoData = useMemo(() => ({
    // Primary Meta Tags
    title: "AI Selfie Generator | Create Stunning AI Portraits & Avatars | [Your Brand]",
    description: "Transform your selfies into professional portraits, artistic avatars, and fun characters with AI. Choose from 100+ styles and create unique profile pictures in seconds. Try free online!",
    keywords: [
      "AI selfie generator", 
      "AI portrait maker", 
      "avatar creator AI", 
      "AI profile picture generator", 
      "digital selfie editor", 
      "AI photo stylizer", 
      "selfie to art converter",
      "AI avatar maker",
      "AI for profile pictures",
      "AI for social media",
      "AI for LinkedIn photos",
      "AI for Instagram",
      "AI for gaming avatars",
      "AI for professional headshots"
    ].join(", "),

    // URL Structure
    canonical: "https://yourdomain.com/ai-selfie-generator",
    alternateUrls: [
      { hreflang: "es", href: "https://yourdomain.com/es/generador-selfie-ai" },
      { hreflang: "fr", href: "https://yourdomain.com/fr/generateur-selfie-ai" }
    ],

    // Social Meta
    openGraph: {
      ogTitle: "AI Selfie Generator: Create Stunning Portraits & Avatars | [Your Brand]",
      ogDescription: "Turn your selfies into professional headshots, artistic avatars, and fun characters with AI. Try our free online generator today!",
      ogImage: "https://cdn.yourdomain.com/social/ai-selfie-og.jpg",
      ogVideo: "https://cdn.yourdomain.com/videos/ai-selfie-demo.mp4"
    },
    
    twitterCard: {
      cardType: "summary_large_image",
      twitterSite: "@YourBrandHandle",
      twitterCreator: "@YourCreatorHandle",
      twitterImageAlt: "AI-generated selfie examples with different styles"
    },

    // Structured Data
    schemaJSON: [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "[Your Brand] AI Selfie Generator",
        operatingSystem: ["Web", "iOS", "Android"],
        applicationCategory: ["PhotoEditor", "AvatarCreator"],
        screenshot: "https://cdn.yourdomain.com/interface/selfie-tool-ui.jpg",
        softwareVersion: "2.5",
        downloadUrl: "https://yourdomain.com/download",
        featureList: [
          "100+ artistic styles",
          "HD portrait generation",
          "Real-time preview",
          "Customizable avatars",
          "Social media-ready outputs",
          "AI-powered face enhancement",
          "Background removal",
          "Style transfer"
        ],
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: "0",
          highPrice: "19.99",
          offerCount: "3",
          offers: [
            {
              "@type": "Offer",
              name: "Free Plan",
              price: "0",
              description: "5 selfies/month with watermark"
            },
            {
              "@type": "Offer",
              name: "Pro Monthly",
              price: "9.99",
              subscription: "monthly"
            },
            {
              "@type": "Offer",
              name: "Pro Annual",
              price: "99.99",
              subscription: "annual"
            }
          ]
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          ratingCount: "4500",
          bestRating: "5",
          worstRating: "1",
          reviewCount: "380"
        }
      },
      {
        "@type": "HowTo",
        name: "How to Create AI Selfies",
        step: [
          {
            "@type": "HowToStep",
            text: "Upload your selfie or take a new photo"
          },
          {
            "@type": "HowToStep",
            text: "Choose from 100+ artistic styles or customize your avatar"
          },
          {
            "@type": "HowToStep",
            text: "Adjust settings like lighting, background, and facial features"
          },
          {
            "@type": "HowToStep",
            text: "Generate and download your AI-enhanced selfie in HD"
          }
        ]
      },
    ],

    // Performance Optimization
    preloadAssets: [
      { href: "https://cdn.yourdomain.com/css/ai-tools.min.css", as: "style" },
      { href: "https://cdn.yourdomain.com/js/ai-selfie.min.js", as: "script" }
    ],

    // Security/Trust
    trustMarkers: {
      secureConnection: true,
      privacyPolicy: "https://yourdomain.com/privacy",
      dataProtection: "GDPR compliant",
      awards: ["2023 Best AI Selfie Tool", "Editors Choice 2024"]
    },

    // Mobile Optimization
    mobileSettings: {
      ampUrl: "https://yourdomain.com/amp/ai-selfie-generator",
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#1a237e"
    },

    // Additional Metadata
    author: "[Your Brand]",
    publisher: "[Your Brand]",
    robots: "index, follow",
    revisitAfter: "7 days",
    language: "English",
    geo: {
      placeName: "San Francisco, CA",
      region: "US-CA",
      position: "37.7749;-122.4194"
    }
  }), []); // Empty dependency array ensures this is memoized once
  // Refs declarations
  const sectionRefs = useRef([]);
  const gradientRef = useRef(null);
  const headingRef = useRef([]);
  const imageRef = useRef(null);
  const buttonRef = useRef(null);
  const featureRefs = useRef([]);
  const videoRefs = useRef([]);
  const sectionHeadingRef = useRef(null);
  const heroContentRef = useRef(null);
  const descriptionRef = useRef(null); // New ref for description
  const navigate=useNavigate()

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Text animations
      gsap.fromTo(
        headingRef.current,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          stagger: 0.3,
          ease: "power4.out",
          scrollTrigger: {
            trigger: heroContentRef.current,
            start: "top 80%",
            end: "top 50%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Gradient text specific animation
      gsap.fromTo(
        headingRef.current[0],
        {
          backgroundPosition: "100% 100%",
        },
        {
          backgroundPosition: "0% 100%",
          duration: 2,
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: headingRef.current[0],
            start: "top 80%",
            end: "top 30%",
            scrub: 1,
          },
        }
      );

      // Stroke text animation
      gsap.fromTo(
        headingRef.current[1],
        {
          textStrokeWidth: "0px",
          opacity: 0,
        },
        {
          textStrokeWidth: "3px",
          opacity: 1,
          duration: 1.5,
          ease: "power4.out",
          scrollTrigger: {
            trigger: headingRef.current[1],
            start: "top 85%",
            end: "top 60%",
            scrub: 1,
          },
        }
      );

      // Description animation
      gsap.fromTo(
        descriptionRef.current,
        {
          opacity: 0,
          y: 30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: descriptionRef.current,
            start: "top 90%",
            end: "top 70%",
            toggleActions: "play none none reverse",
          },
        }
      );
      // Gradient position animation
      gsap.fromTo(
        gradientRef.current,
        { backgroundPosition: "0% 0%" },
        {
          backgroundPosition: "100% 100%",
          ease: "power2.inOut",
          scrollTrigger: {
            start: "top top",
            end: "bottom bottom",
            scrub: 1.5,
          },
        }
      );

      // Color intensity animation
      gsap.fromTo(
        gradientRef.current,
        {
          backgroundImage:
            "linear-gradient(315deg, #0a0c24 0%, #1a1b3a 50%,rgb(23, 5, 24) 100%)",
        },
        {
          backgroundImage:
            "linear-gradient(315deg, #050814 0%, #0f1028 50%, #1a081b 100%)",
          ease: "power2.inOut",
          scrollTrigger: {
            start: "top center",
            end: "bottom center",
            scrub: 1,
          },
        }
      );
      // Hero section animations
      gsap.from(imageRef.current, {
        scale: 1.2,
        duration: 1.5,
        ease: "power4.out",
        transformOrigin: "center center",
      });

      const heroTl = gsap.timeline({
        scrollTrigger: {
          trigger: heroContentRef.current,
          start: "top top",
          end: "+=100%",
          scrub: 1.5,
          pin: true,
          anticipatePin: 1,
        },
      });

      heroTl
        .to(imageRef.current, {
          scale: 1.8,
          y: 200,
          opacity: 0,
          ease: "power3.inOut",
        })
        .to(
          heroContentRef.current,
          {
            y: -200,
            opacity: 0,
            ease: "power3.inOut",
          },
          0
        );

      // Feature section animations
      // Section heading animation
      gsap.fromTo(
        sectionHeadingRef.current,
        { y: 50, opacity: 0, filter: "brightness(1)" },
        {
          y: 0,
          opacity: 1,
          filter: "brightness(1.5)",
          duration: 1.5,
          scrollTrigger: {
            trigger: sectionHeadingRef.current,
            start: "top 90%",
            end: "top 60%",
            scrub: 1.5,
            toggleActions: "play none none reverse",
          },
        }
      );

      // Feature sections animations
      features.forEach((feature, index) => {
        const video = videoRefs.current[index];
        const content = featureRefs.current[index];
        const videoContainer = video.parentElement;
        const videoMask = videoContainer.querySelector(".videoMask");
        const direction = feature.direction === "left" ? -1 : 1;

        // Create timeline for coordinated animations
        const videoTl = gsap.timeline({
          scrollTrigger: {
            trigger: videoContainer,
            start: "top bottom-=10%",
            end: "top center",
            scrub: 1.5,
            toggleActions: "play pause resume reset",
          },
        });

        // Video entrance animation
        videoTl
          .fromTo(
            video,
            { scale: 0.9, opacity: 0 },
            { scale: 1, opacity: 1, duration: 1.2 }
          )
          .fromTo(videoMask, { opacity: 1 }, { opacity: 0, duration: 1.5 }, 0);

        // Content animation
        gsap.fromTo(
          content,
          { x: 150 * direction, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            scrollTrigger: {
              trigger: content,
              start: "top 85%",
              end: "top 50%",
              scrub: 1.2,
            },
          }
        );

        // Reverse mask animation on scroll up
        gsap.fromTo(
          videoMask,
          { opacity: 0 },
          {
            opacity: 1,
            scrollTrigger: {
              trigger: videoContainer,
              start: "center center",
              end: "bottom top",
              scrub: 1,
              toggleActions: "play pause resume reset",
            },
          }
        );

        // Content slide animation
        gsap.fromTo(
          content,
          { x: 150 * direction, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            scrollTrigger: {
              trigger: content,
              start: "top 85%",
              end: "top 50%",
              scrub: 1.2,
              toggleActions: "play none none reverse",
            },
          }
        );

        // Reverse animations on scroll up
        gsap.fromTo(
          video,
          { scale: 1 },
          {
            scale: 0.9,
            opacity: 0,
            scrollTrigger: {
              trigger: video,
              start: "top center",
              end: "bottom top",
              scrub: 1,
              toggleActions: "restart pause resume reset",
            },
          }
        );
      });
    });
    return () => ctx.revert();
  }, []);

  // Updated features array for AI Selfie Generator
  const features = [
    {
      title: "100+ Artistic Styles",
      content:
        "Choose from a wide range of styles, including professional portraits, artistic avatars, and fun characters. Perfect for social media, gaming, and virtual meetings.",
      video: "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "left",
    },
    {
      title: "HD Portrait Generation",
      content:
        "Generate high-resolution portraits with realistic details. Ideal for professional profiles, social media, and print materials.",
      video: "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "right",
    },
    {
      title: "Real-Time Preview",
      content:
        "See your selfie transform in real-time as you apply different styles and effects. Perfect for experimenting with looks before finalizing.",
      video: "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "left",
    },
    {
      title: "Customizable Avatars",
      content:
        "Create unique avatars for gaming, virtual meetings, or social media. Customize features, outfits, and accessories for a personalized touch.",
      video: "https://cdn.web.imagine.art/imagine-frontend/assets/video/text-to-video.webm",
      direction: "right",
    },
  ];

  return (
    <Box className={styles.mainContainer} ref={gradientRef}>
      {/* Added SEO Head Section */}
         <Helmet>
        <html lang="en" />
        <meta charSet="utf-8" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta
          property="og:image"
          content="https://cdn.photofix.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta
          name="twitter:image"
          content="https://cdn.photofix.com/twitter-card.jpg"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(seoData.schemaJSON)}
        </script>

        {/* Additional SEO Enhancements */}
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
        <meta name="robots" content="index, follow" />
        <link rel="alternate" hreflang="x-default" href={seoData.canonical} />
      </Helmet>
      <Navbar />
      <Container className={styles.contentContainer}>
        <Box className={styles.textContainer}>
          <Typography variant="h1" className={styles.heading}>
            <span
              ref={(el) => (headingRef.current[0] = el)}
              className={styles.gradientText}
            >
              AI Selfie Generator:
            </span>
            <br />
            <span
              ref={(el) => (headingRef.current[1] = el)}
              className={styles.strokeText}
            >
              Transform Your Selfies with AI
            </span>
          </Typography>

          <Typography
            variant="subtitle1"
            className={styles.description}
            ref={descriptionRef} // Added ref here
          >
            Create stunning portraits, avatars, and fun characters with
            AI-powered tools. Perfect for social media, gaming, and virtual
            meetings. Try it free today!
          </Typography>

          <Box className={styles.buttonContainer}>
            <Button
              variant="contained"
              className={styles.generateButton}
              ref={buttonRef}
              onClick={()=>navigate("/dashboard/ai-selfie-generator")}
            >
              <span className={styles.buttonText}>
                Try Selfie Generator Now
              </span>
              <div className={styles.buttonGlow} />
            </Button>
          </Box>
        </Box>

        <Box className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
         <img
              ref={imageRef}
              src="https://cdn.web.imagine.art/imagine-frontend/assets/images/ai-image-generator-hero-image.png"
              alt="AI Art Generator Platform Creating Digital Paintings"
              title="AI Art Generator Platform Creating Digital Paintings"
              class={styles.image}
              width="1200"
              height="630"
              loading="lazy"
              decoding="async"
              aria-describedby="image-caption"
            />
            <div className={styles.imageGlow} />
            <div className={styles.imageShine} />
          </div>
        </Box>
      </Container>
      <ImageSlider />
      <ImageSlider2 />
      <Container maxWidth="xl" className={styles.featureContainer}>
        <div ref={sectionHeadingRef}>
          <h2 className={styles.sectionHeading}>
            Why Choose Our AI Selfie Generator?
          </h2>
          <p className={styles.sectionSubheading}>
            Professional tools for creators, gamers, and social media
            enthusiasts
          </p>
        </div>

        {features.map((feature, index) => (
          <section
            key={feature.title}
            ref={(el) => (sectionRefs.current[index] = el)}
            className={styles.featureSection}
          >
            <Grid container spacing={6} alignItems="center">
              <Grid
                item
                xs={12}
                md={6}
                order={{ xs: 2, md: index % 2 ? 2 : 1 }}
              >
                <div
                  ref={(el) => (featureRefs.current[index] = el)}
                  className={`${styles.contentWrapper} feature-content`}
                >
                  <Typography variant="h2" className={styles.featureTitle}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" className={styles.featureText}>
                    {feature.content}
                  </Typography>
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                order={{ xs: 1, md: index % 2 ? 1 : 2 }}
              >
                <div className={styles.videoContainer}>
                  <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className={styles.featureVideo}
                  >
                    <source src={feature.video} type="video/webm" />
                  </video>
                  <div className={styles.videoMask} />
                </div>
              </Grid>
            </Grid>
          </section>
        ))}
      </Container>
      <PricingCards />
      <DownloadSection />
      <FAQ />
      <Footer />
    </Box>
  );
};

export default AISelfieGenerator;
